// A required guardrail's page, `guardrails/required/<rule>`
// (02-DESIGN/13-blueprints/03-required.md § required rules, § the console;
// 01-the-console.md § conventions: a thing's detail is behind its row;
// decision 0069): what it guards, who requires it, what it does, and its
// expression drawn whole. It has no switch and no form: in a managed
// workspace it is changed only in its parent's blueprint; in a solo one its
// admins may *Remove* it, from the page's menu as from its row's. Its address
// is apart from the workspace's own rules' — a required rule and an own rule
// may share a name.

import { useNavigate, useParams, Link } from "react-router";
import { useState, type ReactNode } from "react";

import { tenantPath } from "../../address/address.ts";
import { Empty, NotAllowed, PageHead, Pending, useLoad } from "../../ui/components.tsx";
import { RowMenu, type MenuEntry } from "../../ui/table.tsx";
import {
  requiredListSubject,
  requiredSubject,
  SERVICE,
  type RuleEntry,
  type RulesReply,
} from "../../wire/guard.ts";
import { useSurface } from "../surface.tsx";
import { useManaged } from "../workspaces/managed.ts";
import { GuardRefusal } from "./Unserved.tsx";
import { guards, ruleDoes } from "./words.ts";
import "../../ui/density.css";

export function RequiredGuardrailPage() {
  const { id = "" } = useParams();
  const s = useSurface();
  const [list] = useLoad("required-guardrails", () =>
    s.bus.call<RulesReply>(SERVICE, requiredListSubject(), undefined),
  );
  const entry =
    list.state === "ready" ? list.value.rules.find((e) => e.op === "put" && e.rule.id === id) : undefined;
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "guardrails")}>Guardrails</Link>
      <span>/</span>
      <span>{id}</span>
    </>
  );
  if (list.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading the guardrail" />
      </>
    );
  }
  if (list.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title={id} />
        <GuardRefusal
          err={list.err}
          what="This guardrail"
          absent="The guardrails service does not run in this install, so no guardrail can be read."
        />
      </>
    );
  }
  if (entry === undefined) {
    return (
      <>
        <PageHead crumb={crumb} title={id} />
        <Empty>No required guardrail by this name.</Empty>
      </>
    );
  }
  return <One entry={entry} crumb={crumb} />;
}

function One({ entry, crumb }: { entry: RuleEntry; crumb: ReactNode }) {
  const s = useSurface();
  const navigate = useNavigate();
  const managed = useManaged();
  const [err, setErr] = useState<unknown>(undefined);
  const r = entry.rule;
  const by = managed.managed ? managed.by : `${s.name}'s admins`;
  const entries: MenuEntry[] = managed.managed
    ? []
    : [
        {
          label: "Remove",
          danger: true,
          confirm: {
            title: `Remove required guardrail ${r.id}?`,
            consequence: "It stops applying at the next delivery to every workload.",
          },
          onSelect: () => {
            setErr(undefined);
            s.bus.call<RuleEntry>(SERVICE, requiredSubject("delete", r.id), undefined).then(
              () => void navigate(tenantPath(s.tenant, "guardrails")),
              (e: unknown) => {
                setErr(e);
              },
            );
          },
        },
      ];
  return (
    <>
      <PageHead
        crumb={crumb}
        title={r.id}
        line={`Guards ${guards(r.selector)}.`}
        action={<RowMenu label={`More for ${r.id}`} entries={entries} />}
      />
      {err !== undefined && <NotAllowed err={err} />}
      <div className="card">
        <dl className="kv">
          <dt>Required by</dt>
          <dd>{by}</dd>
          <dt>Version</dt>
          <dd>{entry.revision}</dd>
          <dt>Pattern</dt>
          <dd className="mono small">{r.selector}</dd>
          <dt>Does</dt>
          <dd>{ruleDoes(r)}</dd>
        </dl>
        <div className="field" style={{ marginTop: 16 }}>
          <span className="label">The rule</span>
          <pre className="more-block mono">{r.expression}</pre>
        </div>
      </div>
      <p className="small faint">
        {managed.managed
          ? `${managed.by} requires this guardrail: it applies beside ${s.name}'s own, and is changed only in its blueprint.`
          : `${s.name}'s admins require this guardrail of the workspace itself.`}
      </p>
    </>
  );
}
